import { useState, useEffect, ReactElement } from "react";
import { Dialog, DialogContent, Button, Stack, Markdown } from "@altimateai/lego";
import { useNavigate } from "react-router-dom";
import { BigqueryIconRaw, DatabricksIconRaw } from "@/assets/images";
import {
  AirflowIconRaw,
  DbtIconUrl,
  JiraIconRaw,
  VSCodeIconUrl,
  SnowflakeIconRaw,
} from "@/assets/icons";

interface Step {
  id: number;
  title: string;
  description: string;
  image: ReactElement;
}

const steps: Step[] = [
  {
    id: 1,
    title: "Expand Your Team With AI Teammates!",
    description: `Build AI teammates that help you accelerate work
<div />

- Debug, Build, Deploy & Test models and pipelines! <u>**[Explore](https://datamates-docs.myaltimate.com/examples/build-test-document-dbt-model/#click-through-demo)**</u>
<div />

- Save Infra Cost by optimizing clusters, jobs, and queries! <u>**[Explore](https://datamates-docs.myaltimate.com/examples/optimize-costs-and-performance/)**</u>
<div />

- Migrate code or entire applications! <u>**[Explore](https://datamates-docs.myaltimate.com/examples/migrate-pyspark-dbt/)**</u>`,
    image: <img src="/static/image-1.svg" alt="Step 1" className="al-h-[350px] al-w-auto" />,
  },
  {
    id: 2,
    title: "Choose Tools to Connect Your AI Teammate!",
    description: `AI teammates work with your tools and coding assistants!
    
**Context** and **Guardrails** from your data stack unified in your interface of choice! 
<br/>
<br/>
<span class="al-text-brand al-font-semibold">All connections happen locally on your machine —— nothing is sent to our platform.</span>  
<br/>
<div class="al-flex al-gap-2 al-flex-wrap">
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><span class="[&>svg]:al-w-5 [&>svg]:al-h-5">${SnowflakeIconRaw}</span> Snowflake</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><img class="al-w-5 al-h-5" src="${DbtIconUrl}" alt="dbt" /> dbt</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><img class="al-w-5 al-h-6" src="${VSCodeIconUrl}" alt="vscode" /> VS Code</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><span class="[&>svg]:al-w-5 [&>svg]:al-h-5">${AirflowIconRaw}</span> Airflow</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><span class="[&>svg]:al-w-5 [&>svg]:al-h-5">${DatabricksIconRaw}</span> Databricks</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><i class="codicon codicon-github-inverted al-w-4"></i> Github</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><span class="[&>svg]:al-w-5 [&>svg]:al-h-5">${BigqueryIconRaw}</span> Big Query</div>
<div class="al-flex al-items-center al-gap-2 al-border al-rounded-full al-p-2 al-text-sm"><span class="[&>svg]:al-w-5 [&>svg]:al-h-5">${JiraIconRaw}</span> Jira</div>
</div>
<br/>

and many more <u>**[Integrations](https://datamates-docs.myaltimate.com/user-guide/components/integrations/integrations/)**</u>
`,
    image: <img src="/static/image-2.svg" alt="Step 2" className="al-h-[350px] al-w-auto" />,
  },
  {
    id: 3,
    title: "Code like you!",
    description: `For any AI agent to truly code like you, it needs verified, detailed context about the problem statement. With Datamates you can coach your AI teammates using -
- **Knowledge**: You can choose to attach your organizational knowledge to AI teammates from Google Docs, Confluence, Wikis, PDF, etc. <u>**[Explore](https://datamates-docs.myaltimate.com/user-guide/components/knowledgehub/)**</u>
<br/>

- **Memory**: Memory helps to capture and reference coding insights, architectural decisions, and tribal knowledge with every interaction <u>**[Explore](https://datamates-docs.myaltimate.com/user-guide/components/memory/)**</u>`,
    image: <img src="/static/image-3.svg" alt="Step 3" className="al-h-[350px] al-w-auto" />,
  },
  {
    id: 4,
    title: "Get Ready 🚀 !",
    description: `The platform comes bundled with some AI teammates and you can create your own as well!
<br/>

Quicklinks - 

<u>**[Setup Guide](https://datamates-docs.myaltimate.com/user-guide/setup/setup/)**</u>

<u>**[Examples](https://datamates-docs.myaltimate.com/examples/examples/)**</u>
    `,
    image: <img src="/static/image-4.svg" alt="Step 4" className="al-h-[350px] al-w-auto" />,
  },
];

export const OnboardDatamate = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onboarded = localStorage.getItem("datamateOnboarded");
    if (!onboarded) {
      setOpen(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      localStorage.setItem("datamateOnboarded", "true");
      setOpen(false);
      navigate("/datamates/create");
    }
  };

  const handleClose = () => {
    localStorage.setItem("datamateOnboarded", "true");
    setOpen(false);
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          handleClose();
        } else {
          setOpen(isOpen);
        }
      }}
    >
      <DialogContent className="!al-max-w-[900px] al-w-[50vw] al-min-w-[700px]">
        <Stack direction="column" className="al-w-full al-p-0">
          {/* Content */}
          <Stack className="al-text-center" align="center" justify="between">
            {/* Image */}
            <Stack className=" al-text-left" direction="column">
              {/* Title */}
              <h2 className="al-text-2xl al-font-semibold al-mb-4">{currentStepData.title}</h2>

              {/* Description */}
              <p className="al-text-gray-600 al-mb-8 al-max-w-md al-text-body">
                <Markdown content={currentStepData.description} />
              </p>

              {/* Buttons */}
              <Stack direction="row" className="al-gap-4 al-w-full">
                <Button onClick={handleNext} className="al-bg-blue-500 hover:al-bg-blue-600">
                  {isLastStep ? "Get Started" : "Next"}
                </Button>
              </Stack>
            </Stack>
            <Stack align="center" justify="center" className="al-mb-6 ">
              {currentStepData.image}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
